export const USER_ROLE = {
  GUEST: 'guest',
  OWNER: 'owner',
  HOUSEKEEPING: 'housekeeping',
  RECEPTIONIST: 'receptionist',
  ADMIN: 'admin',
} as const;

export type UserRole = (typeof USER_ROLE)[keyof typeof USER_ROLE];

/**
 * Firebase Auth custom claims. Set exclusively by trusted server code
 * (the seed/admin script or an admin-only Cloud Function) and readable from
 * Firestore Security Rules via `request.auth.token`.
 *
 * Guests are anonymous and carry no claims — their room binding lives in the
 * `GuestSessionDoc` instead, so it can expire without minting a new token.
 */
export interface ReceptionistClaims {
  role: typeof USER_ROLE.RECEPTIONIST;
  hotelId: string;
  /** Always equal to the Firebase UID; the receptionist doc id mirrors it. */
  receptionistId: string;
}

export const RECEPTIONIST_STATUS = {
  ONLINE: 'ONLINE',
  OFFLINE: 'OFFLINE',
  BUSY: 'BUSY',
} as const;

export type ReceptionistStatus =
  (typeof RECEPTIONIST_STATUS)[keyof typeof RECEPTIONIST_STATUS];

/** `hotels/{hotelId}/receptionists/{receptionistId}` — doc id === Firebase UID. */
export interface ReceptionistDoc {
  name: string;
  email?: string;
  status: ReceptionistStatus;
  /** Soft-disable switch controlled by hotel admins, independent of presence. */
  active: boolean;
  /**
   * True when this row belongs to the hotel **owner**, who opted in to taking
   * guest calls rather than being hired into it.
   *
   * The owner holding a receptionist document is the whole mechanism behind
   * that toggle: routing, `requireReceptionist`, presence and device
   * registration all key off this collection, so opting in is creating the row
   * and opting out is clearing its `active` flag. Nothing else had to learn
   * about owners.
   *
   * The flag itself grants nothing — `active` does. It exists so a staff list
   * can tell the owner apart from someone who was hired, and so
   * `updateStaffMember` can refuse to touch a row that has no matching staff
   * document behind it.
   */
  isOwner?: boolean;
  /** Set while a call is in flight so routing can skip this receptionist. */
  currentCallId?: string | null;
  departments?: string[];
  lastSeenAt?: unknown;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export const DEVICE_PLATFORM = {
  ANDROID: 'android',
  IOS: 'ios',
  WEB: 'web',
} as const;

export type DevicePlatform =
  (typeof DEVICE_PLATFORM)[keyof typeof DEVICE_PLATFORM];

/**
 * `hotels/{hotelId}/receptionists/{receptionistId}/devices/{deviceId}`
 *
 * A collection rather than a single field on the receptionist doc, so one
 * receptionist can be signed in on several handsets and every one of them rings.
 */
export interface DeviceDoc {
  token: string;
  platform: DevicePlatform;
  active: boolean;
  appVersion?: string;
  lastSeenAt?: unknown;
  createdAt?: unknown;
}

/* -------------------------------------------------------------------------- */
/* Hotel management claims                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Claims carried by everyone who signs into the management app.
 *
 * `role` is the routing and authorization key. A Receptionist keeps
 * `role: 'receptionist'` and a `receptionistId` — byte-for-byte what the call
 * system already requires (`requireReceptionist`, and `claims().role ==
 * 'receptionist'` in firestore.rules) — so adding staff management changed
 * nothing about who may take a guest call.
 *
 * `onboarded` is false for a brand-new owner until `completeHotelSetup` runs,
 * which is what routes them to the setup screen exactly once.
 */
export interface HotelUserClaims {
  role: UserRole;
  hotelId?: string;
  /** Present for receptionists only; always equal to the Firebase UID. */
  receptionistId?: string;
  /** Present for staff (receptionist + housekeeping); equal to the Firebase UID. */
  staffId?: string;
  onboarded?: boolean;
}

/** Roles that belong to a hotel's own staff, as opposed to guests. */
export const HOTEL_STAFF_ROLES: readonly UserRole[] = [
  USER_ROLE.RECEPTIONIST,
  USER_ROLE.HOUSEKEEPING,
];

export function isHotelMemberRole(role: unknown): boolean {
  return role === USER_ROLE.OWNER || HOTEL_STAFF_ROLES.includes(role as UserRole);
}
